import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import styles from './CustomizeCollections.module.scss';
import { SelectOption } from '../../types';
import Spinner from 'src/components/spinner/Spinner';
import Button from 'src/components/button/Button';
import {
  useSllGetLessonsForSaLazyQuery,
  SllLesson,
  AusCourseContent,
  CourseContentType,
} from '../../api/graphql-codegen';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import IconPencil from 'src/assets/icons/pencil.svg?react';
import CoverLesson from '../CoverLesson/CoverLesson';
import { useToast } from "@src/hooks/useToast";
import CollectionFilterLayout from './FilterLayout';
import LessonsLayout from './dndLessonLayout/LessonsLayout';
import { Collapse } from 'react-collapse';
import EditCollectionNameModal from './EditCollectionNameModal';
import { debounce } from '@src/utils/helpers';
import Pagination from '@src/components/pagination/Pagination';
import { logger } from '../../utils/logger';

const unitMoveMessage = 'Content is moved successfully!';

type Props = {
  collection?: any;
  loadingCollection?: boolean;
  isDisabled?: boolean;
  handleSave?: (lessons: any[]) => void;
  handleExit?: (lessons: any[]) => void;
}

type lessonQuery = {
  keyword: string
  sortBy: 'name' | 'country'
  order: 'ASC' | 'DESC'
  page: number
  perPage: number
  ageGroup?: string
  isPublished: number
}

const defaultQuery: lessonQuery = {
  keyword: '',
  sortBy: 'name' as 'name' | 'country',
  order: 'ASC' as 'ASC' | 'DESC',
  page: 1,
  perPage: 24,
  isPublished: 1
}

function CustomizeCollections(props: Props) {
  const { collection, isDisabled, loadingCollection, handleSave, handleExit } = props;

  const [country, setCountry] = useState<string>('All');
  const [courses, setCourses] = useState<string>('');
  const [selCollection, setSelCollection] = useState(true);
  const [isMovable, setIsMovable] = useState(false);  // New Cover movable
  const [collectionLessons, setCollectionLessons] = useState<AusCourseContent[]>([]);
  const [editModal, setEditModal] = useState(false);

  const [queries, setQueries] = useState<lessonQuery>(defaultQuery);

  const { successToast } = useToast();

  const [sllGetLessons, {loading, data}] = useSllGetLessonsForSaLazyQuery({fetchPolicy: 'network-only'});

  const debouncedApiCall = useRef(
    debounce((query: lessonQuery) => sllGetLessons({variables: {input: query}}), 500)
  ).current;

  useEffect(() => {
    document.getElementById("root")!.style.overflow = "hidden"
  }, []);

  useEffect(() => {
    if (collection?.sllLessons) {
      setCollectionLessons(collection?.sllLessons ?? [])
    }
  }, [collection?.sllLessons])

  useEffect(() => {
    debouncedApiCall(defaultQuery)
  }, [debouncedApiCall])

  const allLessons = useMemo(() => {
    return data?.sllGetLessonsForSA?.data ?? []
  }, [data?.sllGetLessonsForSA?.data])

  const handleStatesChange = useCallback((key: string, value: any) => {
    setQueries((prev) => {
      const newStates = {
        ...prev,
        [key]: value,
        ...((key === 'keyword' || key === 'ageGroup') && { page: 1 })
      }
      debouncedApiCall(newStates)

      return newStates
    })
  }, [debouncedApiCall])

  const handleResetFilters = useCallback(() => {
    setCountry('');
    
    debouncedApiCall(defaultQuery);
  }, [debouncedApiCall]);

  const handleChangeCountry = useCallback((option: SelectOption) => {
    setCountry(option.value);
  },[])

  const handleChangeCourse = (value: any) => {
    setCourses(value);
  };

  const lessonData = useMemo(() => {
    if (data?.sllGetLessonsForSA) {
      return {
        list: data?.sllGetLessonsForSA?.data ?? [],
        totals: data?.sllGetLessonsForSA.totals
      }
    }
    return {
      list: [],
      totals: 0
    }
  }, [data]);

  const {filteredLessons} = useMemo(() => {
    var filteredLessons = data?.sllGetLessonsForSA?.data ?? []

    if (country && country !== 'All') {
      filteredLessons = filteredLessons.filter(lesson => (lesson?.country || '').includes(country));
    }

    return {filteredLessons}
  }, [country, data?.sllGetLessonsForSA?.data])

  // Remove Grade Lesson
  const handleRemoveAusGradeContent = (content: AusCourseContent) => {
    setCollectionLessons((prev: any) => {
      const preLessons = prev as AusCourseContent[];
      const temp = preLessons.filter(item => item !== content);
      return temp;
    })
  }
  // handle Drag and Drop on Right Side
  const handleDnDOnLessonList = useCallback((index: number, lessons: string[]) => {
    const sortedArray = [...collectionLessons].sort((a, b) => {
      return lessons.indexOf(a.id) - lessons.indexOf(b.id);
    });
    setCollectionLessons(sortedArray);
  }, [collectionLessons]);
  // handle Drag and Drop
  const handleDrop = useCallback(
    (index: number, item: any) => {
      try {
        if (item.lesson) { // Drop with Standalone Lesson

          const dropObj = Object.values(item) && Object.values(item)[0] as SllLesson
          const droppedLesson = allLessons.find(lesson => lesson?.id === dropObj.id)
          
          if (index === 0) {  // Remove lesson
            if (collectionLessons.find(item => item.id === droppedLesson?.id)) {
              const temp = [...collectionLessons];
              setCollectionLessons(temp.filter(u => u.id !== droppedLesson?.id));
            }
          } else if (index === 1) {  // Add Lesson to Main Content
            setCollectionLessons((prev: any) => {
              const preLessons = prev as AusCourseContent[];
              const exist = preLessons.find(item => item.id === droppedLesson?.id)
              if (exist) {
                return [...prev];
              } else {
                const addingContent: AusCourseContent = {
                  id: droppedLesson?.id ?? "",
                  categories: droppedLesson?.categories ?? "",
                  comingSoonAt: droppedLesson?.comingSoonAt ?? "",
                  coverImage: droppedLesson?.coverImage ?? "",
                  gradeLevel: droppedLesson?.gradeLevel ?? "",
                  isComingSoon: droppedLesson?.isComingSoon ?? false,
                  isNew: droppedLesson?.isNew ?? false,
                  isNewCover: droppedLesson?.isNewCover ?? false,
                  isOpReel: droppedLesson?.isOpReel ?? false,
                  isUpdated: droppedLesson?.isUpdated ?? false,
                  name: droppedLesson?.name ?? "",
                  number: 0,
                  progress: 0,
                  subHeading: "",
                  suggestedDuration: droppedLesson?.suggestedDuration ?? "",
                  suggestedLessonCount: 0,
                  type: CourseContentType.LESSON,
                  comingSoonLabelPosition: droppedLesson?.comingSoonLabelPosition ?? "",
                  newLabelPosition: droppedLesson?.newLabelPosition ?? "",
                  updatedLabelPosition: droppedLesson?.updatedLabelPosition ?? ""
                }
                return [...prev, addingContent];
              }
            })
          }
          successToast(unitMoveMessage);
        }
      } catch (e) {
        logger.info(e);
      }
    }, [collectionLessons, allLessons, successToast]
  );

  const handleMovableNewCover = (isNew: boolean) => {
    setIsMovable(isNew)
  }

  const handleEditCollectionName = () => {
    setEditModal(true)
  }

  const handleSelectCollection = () => {
    setSelCollection(current => !current)
  }

  const unitAndLessonContent = (
    <div>{loading ? <div className={styles.spinner}><Spinner /></div> : 
      <>
        <div className={styles.unitsDiv}>
        {filteredLessons.map((lesson, index) => {
          return (
            <CoverLesson
              key={index}
              lesson={lesson as SllLesson}
              type={"type"}
              source={"origin"}
              handleMovableNewCover={handleMovableNewCover}
            />
          )
        })}
        </div>
        <div style={{marginTop: 20, marginBottom: 40}}>
          <Pagination
            perPage={queries.perPage}
            currentPage={queries.page}
            totalItems={lessonData.totals}
            onPageChange={v => handleStatesChange('page', v)}
          />
        </div>
      </>
      }
    </div>
  )

  return (
    <DndProvider backend={HTML5Backend}>
      {(loadingCollection) ? <div className={styles.spinner}><Spinner /></div> : <>
      <div id="customunits" className={styles.page}>
        <div className={styles.leftView}>
          <CollectionFilterLayout
            keyword={queries.keyword}
            course={courses}
            country={country}
            ageGroup={queries.ageGroup}
            gradeSort={false}
            isSingleCollection={true}
            handleChangeAgeGroup={v => handleStatesChange('ageGroup', v)}
            handleChangeCourse={handleChangeCourse}
            handleChangeSearch={v => handleStatesChange('keyword', v)}
            handleChangeCountry={handleChangeCountry}
            handleResetFilters={handleResetFilters}
          />
          {unitAndLessonContent}
        </div>
        <div className={styles.rightView}>
          <div className={styles.actionsBanner}>
            <div className={styles.actionsBanner_buttons}>
              <Button className={styles.actionsBanner_cancel} onClick={() => handleExit && handleExit(collectionLessons)} size={'small'} label={'Exit'} />
              <Button 
                disabled={isDisabled || collectionLessons.length === 0}
                leftIcon={isDisabled ? <Spinner width={20} height={20} /> : undefined} className={styles.actionsBanner_done} 
                onClick={() => handleSave && handleSave(collectionLessons)} size={'small'} label={'Save'} />
            </div>
          </div>
          <div className={styles.lessonList}>
            <div className={styles.planItemContainer}>
              <div className={styles.planItem}>
                <div className={styles.subItem}>
                  <div className={styles.subItemTitle} >{('ABC').substring(0, 1).toLocaleUpperCase()}</div>
                </div>
                <div className={styles.planName} style={{color: `white`}}>
                  {collection?.name ?? ""}<br/>
                  {'Collection'}
                </div>
                <div className={styles.editPlan} onClick={handleEditCollectionName}>
                  <IconPencil fill={`white`} />
                </div>
                <div className={[styles.courseToggle, selCollection ? styles.courseToggleClose : styles.courseToggleOpen].join(' ')} onClick={handleSelectCollection}></div>
              </div>
            </div>
            <Collapse isOpened={selCollection}>
              <div className={styles.collectionLessons}>
                <LessonsLayout
                  source={1}
                  contents={collectionLessons}
                  isNewMovable={isMovable}
                  handleDrop={handleDrop}
                  handleRemoveContentAction={handleRemoveAusGradeContent}
                  handleDnDOnLessonList={handleDnDOnLessonList}
                />
              </div>
            </Collapse>
          </div>
        </div>
      </div>
      {editModal && <EditCollectionNameModal open={editModal} collection={collection} onClose={()=>setEditModal(false)}/>}
      </>}
    </DndProvider>
  );
}

export default CustomizeCollections;
